/** The envoy's queue, what it finished, and what waits on the person. */

import type { TailorChoices } from "@/lib/posting/tailorChoices";
import { deleteJson, getJson, pathSegment, postJson, queryString } from "../client";
import type { Application } from "./applications";
import { ENVOY_BASE } from "./base";

/** What stops the envoy as a whole. */
export type EnvoyCondition = "PAUSED" | "HELD" | "OUT_OF_TAILORS" | "SLOWED";

export interface QueueEntry {
    posting_id: string;
    queued_by: string | null;
    queued_at: string | null;
    waiting_on: EnvoyCondition | null;
    origin: "PERSON" | "ENVOY";
    place?: number | null;
    application: Application;
}

export interface Queue {
    next_cursor?: string | null;
    entries: QueueEntry[];
}

export interface ReadyEntry {
    questions: string[];
    application: Application;
}

export interface ReadyList {
    next_cursor?: string | null;
    applications: ReadyEntry[];
}

export interface NeedsYouEntry {
    /** `NOT_TAILORED` or `GAVE_UP`. */
    reason: string;
    questions: string[];
    application: Application;
}

export interface NeedsYouList {
    next_cursor?: string | null;
    conditions: EnvoyCondition[];
    applications: NeedsYouEntry[];
}

export type QueuePageParams = { limit?: number; cursor?: string };

export interface QueueHistoryEntry {
    posting_id: string;
    application_id: string;
    title: string;
    company: string;
    action: string;
    by: string;
    at: string;
    status: string;
}
export interface QueueHistory {
    entries: QueueHistoryEntry[];
    next_cursor?: string | null;
}
export function getQueue(params: QueuePageParams = {}): Promise<Queue> {
    return getJson(`${ENVOY_BASE}/queue${queryString(params)}`);
}
export function getQueueHistory(params: QueuePageParams = {}): Promise<QueueHistory> {
    return getJson(
        `${ENVOY_BASE}/queue/history${queryString({ ...params, completed_only: true })}`,
    );
}

export function getReady(limit?: number, cursor?: string): Promise<ReadyList> {
    return getJson(`${ENVOY_BASE}/ready${queryString({ limit, cursor })}`);
}

export function getNeedsYou(limit?: number, cursor?: string): Promise<NeedsYouList> {
    return getJson(`${ENVOY_BASE}/needs-you${queryString({ limit, cursor })}`);
}

/**
 * Hands a posting to the envoy under its standing order, with what the
 * person chose for its resume. A choice left alone is not sent.
 */
export function queuePosting(postingId: string, choices?: TailorChoices): Promise<QueueEntry> {
    return postJson(`${ENVOY_BASE}/queue`, {
        posting_id: postingId,
        ...(choices?.instructions !== undefined && { instructions: choices.instructions }),
        ...(choices && choices.keywordsOff.length > 0 && { keywords_off: choices.keywordsOff }),
    });
}

export async function unqueuePosting(postingId: string): Promise<QueueEntry> {
    return deleteJson(`${ENVOY_BASE}/queue/${pathSegment(postingId)}`);
}
